import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Form, Input, message } from 'antd';
import useBoolean from 'common/hooks/useBoolean';
import { useMutation } from '@apollo/client';
import { accountActionTypes } from '~constants/actionTypes';
import { MUTATION_UPDATE_LANDLORD } from '../queries';

const uesStyles = makeStyles(
  theme => ({
    wrap: {
      width: '50%',
      marginTop: 24,
      paddingBottom: 24,
      boxShadow: theme.divider.dividerBottom,

      '& .ant-form-item': {
        marginBottom: 16,
      },
    },
    label: {
      ...theme.typography.h5,
      color: theme.colors.titleColor,
      margin: 0,
    },
    value: {
      ...theme.typography.body2,
      color: theme.colors.bodyColor,
    },
    title: {
      ...theme.flex.mainSpaceBetween,
      marginBottom: 6,
    },
    editBtn: {
      ...theme.typography.h5,
      height: 22,
      padding: 0,
      color: theme.colors.mainGreen6,
      border: '0 !important',
      boxShadow: 'none !important',
    },
    input: {
      height: 40,
      marginTop: 10,
    },
  }),
  { name: 'FieldCard' },
);

const ReadOnlyFieldCard = ({ label, value }) => {
  const classes = uesStyles();
  return (
    <>
      <h5 className={classes.label} style={{ marginBottom: 6 }}>
        {label}
      </h5>
      <span className={classes.value}>{value}</span>
    </>
  );
};

ReadOnlyFieldCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

const FieldCard = ({
  label,
  value,
  type,
  id,
  readOnly,
  enabled,
  form: { getFieldDecorator, validateFields },
}) => {
  const classes = uesStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { landlordUser } = useSelector(state => ({
    landlordUser: state.pmsReducer.dashboard.account.toJS().account,
  }));

  const [edit, { toggle: setEdit }] = useBoolean(false);

  const [mutationUpdateLandlordContact, { loading }] = useMutation(MUTATION_UPDATE_LANDLORD, {
    onCompleted: data => {
      const updateValue = data?.updateLandlordContact?.landlordContact?.[type];

      setEdit();
      dispatch({
        type: accountActionTypes.SET_CMS_USER,
        payload: { ...landlordUser, [type]: updateValue },
      });
    },
    onError: e => {
      message.error(e?.toString());
    },
  });

  const handleConfirm = () => {
    validateFields((err, values) => {
      if (!err) {
        mutationUpdateLandlordContact({ variables: { input: { id, ...values } } });
      }
    });
  };

  return (
    <div className={classes.wrap}>
      <Choose>
        <When condition={readOnly}>
          <ReadOnlyFieldCard label={label} value={value} />
        </When>
        <Otherwise>
          <div className={classes.title}>
            <h5 className={classes.label}>{label}</h5>
            <Button
              ghost
              className={classes.editBtn}
              onClick={setEdit}
              disabled={!enabled || loading}
            >
              {edit ? t('cancel') : t('edit')}
            </Button>
          </div>
          <div>
            <Choose>
              <When condition={!edit}>
                <span className={classes.value}>{value}</span>
              </When>
              <Otherwise>
                <Form hideRequiredMark>
                  <Form.Item>
                    {getFieldDecorator(type, {
                      initialValue: value,
                      rules: [
                        {
                          required: true,
                          message: t('this_part_cannot_be_empty'),
                        },
                      ],
                    })(<Input placeholder={label} className={classes.input} />)}
                  </Form.Item>
                </Form>
                <Button
                  type="primary"
                  onClick={handleConfirm}
                  loading={loading}
                  style={{ height: 40, fontWeight: 'bold' }}
                >
                  {t('confirm')}
                </Button>
              </Otherwise>
            </Choose>
          </div>
        </Otherwise>
      </Choose>
    </div>
  );
};

FieldCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
  form: PropTypes.object,
  enabled: PropTypes.bool,
  type: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
};

export default Form.create({
  name: 'FieldCard',
})(FieldCard);
